<template>
  <v-chip
    :size="size"
    class="status-chip"
    :class="toneClass"
    :variant="variant"
  >
    <v-progress-circular
      v-if="pulse"
      indeterminate
      :size="iconSize"
      width="2"
      class="status-chip__icon"
    />
    <v-icon
      v-else-if="icon"
      :size="iconSize"
      class="status-chip__icon"
      start>
      {{ resolvedIcon }}
    </v-icon>
    <slot>{{ displayLabel }}</slot>
  </v-chip>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { getStatusMeta } from '@/utils/statusIcon'

export default defineComponent({
  name: 'StatusChip',
  props: {
    label: {
      type: String,
      default: '',
    },
    tone: {
      type: String,
      default: 'neutral',
    },
    size: {
      type: String,
      default: 'small',
    },
    variant: {
      type: String,
      default: 'flat',
    },
    icon: {
      type: Boolean,
      default: false,
    },
    iconSize: {
      type: [String, Number],
      default: 14,
    },
    // 진행 중 같은 "지금 움직이는 상태"를 보여주기 위해 아이콘 자리에 도는 스피너를 쓴다.
    // 데이터가 안 와도 "멈춘 게 아니라 계속 보고 있다"는 걸 전달하는 용도(아이콘은 켜져 있어도 숨김).
    pulse: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    statusMeta(): { tone: string; icon: string; label: string } {
      return getStatusMeta(this.tone || this.label)
    },
    resolvedTone(): string {
      const raw = String(this.tone || '').toLowerCase().trim()
      if (['custom', 'inherit'].includes(raw)) {
        return 'custom'
      }
      const tone = this.statusMeta.tone
      if (tone === 'success') return 'ready'
      if (tone === 'info') return 'active'
      if (tone === 'warning') return 'warning'
      if (tone === 'danger') return 'danger'
      return 'neutral'
    },
    toneClass(): string {
      return `status-chip--${this.resolvedTone}`
    },
    resolvedIcon(): string {
      return this.statusMeta.icon
    },
    displayLabel(): string {
      if (this.label) {
        return this.label
      }
      return this.statusMeta.label
    },
  },
})
</script>

<style scoped>
.status-chip {
  font-weight: 600;
  border: 1px solid transparent;
}

.status-chip__icon {
  margin-inline-start: -2px;
  margin-inline-end: 4px;
}

.status-chip--active {
  background: #eaf2ff;
  color: #2563eb;
  border-color: #bfdbfe;
}

.status-chip--ready {
  background: #edfdf3;
  color: #16a34a;
  border-color: #bbf7d0;
}

.status-chip--neutral {
  background: #f1f5f9;
  color: #475569;
  border-color: #cbd5e1;
}

.status-chip--warning {
  background: #fff4e6;
  color: #b45309;
  border-color: #fed7aa;
}

.status-chip--danger {
  background: #feeaea;
  color: #dc2626;
  border-color: #fecaca;
}

.status-chip--custom {
  border-color: transparent;
}
</style>
